import React, { useState } from "react";
import { toast } from "sonner";
import { PageMeta } from "@/components/PageMeta";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { CtaBand } from "@/components/CtaBand";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Phone, Mail, MessageCircle, MapPin, Clock } from "lucide-react";
import { BRAND, CONTACT, waLink } from "@/config/brand";
import { CONTACT_IDS } from "@/constants/testIds";
import { api } from "@/lib/api";

export default function Contact() {
    const [v, setV] = useState({ name: "", email: "", phone: "", subject: "", message: "", consent: false, website: "" });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!v.consent) return toast.error("Please confirm you’re happy for us to reply.");
        setBusy(true);
        try {
            await api.post("/contact", v);
            setDone(true);
            toast.success("Thanks — we’ll reply shortly.");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not send. Please try WhatsApp or phone.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div>
            <PageMeta title="Contact YS Removals — London" description="Call, WhatsApp or email YS Removals for a friendly, tailored quotation." canonical={`https://${BRAND.domain}/contact`} />
            <section className="bg-[#001B3D] text-white">
                <div className="container-ys section">
                    <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Contact" }]} />
                    <h1 className="mt-3 text-4xl sm:text-5xl font-semibold tracking-tight font-heading">Contact YS Removals</h1>
                    <p className="mt-3 text-white/85 max-w-2xl">We’re a phone call or a message away. Whichever is easiest for you.</p>
                </div>
            </section>
            <section className="container-ys section grid lg:grid-cols-3 gap-8">
                <div className="space-y-4">
                    <div className="rounded-xl border border-[#17252D]/10 p-5">
                        <div className="font-semibold flex items-center gap-2"><Phone className="h-4 w-4 text-[#001B3D]" /> Phone</div>
                        <a href={CONTACT.phoneHref} className="mt-1 block text-lg font-heading text-[#001B3D]">{CONTACT.phoneDisplay}</a>
                    </div>
                    <a href={waLink()} target="_blank" rel="noreferrer" className="block rounded-xl border border-[#25D366]/40 bg-[#25D366]/5 p-5 hover:border-[#25D366] hover:bg-[#25D366]/10 transition-colors duration-200">
                        <div className="font-semibold flex items-center gap-2 text-[#128C7E]"><MessageCircle className="h-4 w-4" /> WhatsApp</div>
                        <div className="mt-1 text-lg font-heading text-[#001B3D]">{CONTACT.whatsappDisplay}</div>
                    </a>
                    <a href={CONTACT.emailHref} className="block rounded-xl border border-[#17252D]/10 p-5 hover:border-[#001B3D]/40">
                        <div className="font-semibold flex items-center gap-2"><Mail className="h-4 w-4 text-[#001B3D]" /> Email</div>
                        <div className="mt-1 text-lg font-heading text-[#001B3D] break-all">{CONTACT.email}</div>
                    </a>
                    <div className="rounded-xl border border-[#17252D]/10 p-5">
                        <div className="font-semibold flex items-center gap-2"><MapPin className="h-4 w-4 text-[#001B3D]" /> Based in</div>
                        <div className="mt-1 text-[#17252D]/80">{CONTACT.addressLine1}, {CONTACT.addressLine2}</div>
                        <div className="mt-3 font-semibold flex items-center gap-2"><Clock className="h-4 w-4 text-[#001B3D]" /> Hours</div>
                        <div className="mt-1 text-[#17252D]/80">{CONTACT.hours}</div>
                    </div>
                </div>
                <div className="lg:col-span-2">
                    {done ? (
                        <div className="rounded-2xl bg-white border border-[#17252D]/10 p-8 text-center">
                            <h2 className="text-2xl font-semibold">Message sent</h2>
                            <p className="mt-2 text-[#17252D]/75">We’ll be in touch soon. For urgent enquiries, WhatsApp is fastest.</p>
                        </div>
                    ) : (
                        <form data-testid={CONTACT_IDS.form} onSubmit={submit} className="rounded-2xl bg-white border border-[#17252D]/10 p-6 sm:p-8 grid gap-4 sm:grid-cols-2">
                            <div><Label>Full name</Label><Input required value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} /></div>
                            <div><Label>Email</Label><Input required type="email" value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} /></div>
                            <div><Label>Phone (optional)</Label><Input value={v.phone} onChange={(e) => setV({ ...v, phone: e.target.value })} /></div>
                            <div><Label>Subject (optional)</Label><Input value={v.subject} onChange={(e) => setV({ ...v, subject: e.target.value })} /></div>
                            <div className="sm:col-span-2"><Label>Message</Label><Textarea required rows={6} value={v.message} onChange={(e) => setV({ ...v, message: e.target.value })} /></div>
                            <input type="text" name="website" tabIndex={-1} autoComplete="off" value={v.website} onChange={(e) => setV({ ...v, website: e.target.value })} className="hidden" />
                            <label className="sm:col-span-2 flex items-start gap-2 text-sm">
                                <Checkbox checked={v.consent} onCheckedChange={(x) => setV({ ...v, consent: !!x })} className="mt-0.5" />
                                <span>I’m happy for YS Removals to contact me about my enquiry.</span>
                            </label>
                            <div className="sm:col-span-2">
                                <Button type="submit" disabled={busy} data-testid={CONTACT_IDS.submit} className="bg-[#001B3D] hover:bg-[#001B3D]/90 text-white h-11 px-6">
                                    {busy ? "Sending…" : "Send message"}
                                </Button>
                            </div>
                        </form>
                    )}
                </div>
            </section>
            <CtaBand />
        </div>
    );
}
